import {
  ArrowLeftIcon,
  BasketIcon,
  CheckIcon,
  CreditCardIcon,
  SpinnerGapIcon,
  UsersThreeIcon,
  WalletIcon,
} from '@phosphor-icons/react'
import type { PayFrequency } from '@paybae/shared'
import { useState } from 'react'

import { Pastilla } from '@/components/Editorial'
import { AmountDisplay, NumPad } from '@/components/NumPad'
import { ApiError, accounts, cards, household, incomes } from '@/lib/api'
import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'

/**
 * Configuración inicial del hogar.
 *
 * Se pide en este orden porque cada paso desbloquea el siguiente: sin nómina no
 * hay periodo de gasto, sin cuentas no hay saldo del que restar, y sin tarjeta
 * no hay fecha de corte que vigilar. El disponible —la cifra por la que existe
 * la app— no puede calcularse hasta tener los tres.
 *
 * Todo lo que no es imprescindible se puede saltar y completar después desde
 * Ajustes. Un formulario largo antes de ver nada es la forma más rápida de que
 * alguien abandone.
 */

type Paso = 'nomina' | 'aportacion' | 'cuenta' | 'vales' | 'tarjeta' | 'listo'

const FRECUENCIAS: ReadonlyArray<{ value: PayFrequency; label: string; detalle: string }> = [
  { value: 'SEMI_MONTHLY', label: 'Quincenal', detalle: 'Dos veces al mes' },
  { value: 'MONTHLY', label: 'Mensual', detalle: 'Una vez al mes' },
  { value: 'BIWEEKLY', label: 'Catorcenal', detalle: 'Cada 14 días' },
  { value: 'WEEKLY', label: 'Semanal', detalle: 'Cada 7 días' },
]

export function Onboarding({
  memberId,
  householdKind,
  onDone,
}: {
  memberId: string
  householdKind: 'PERSONAL' | 'COUPLE' | 'GROUP'
  onDone: () => void
}) {
  const esPersonal = householdKind === 'PERSONAL'
  const [paso, setPaso] = useState<Paso>('nomina')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Nómina
  const [frecuencia, setFrecuencia] = useState<PayFrequency>('SEMI_MONTHLY')
  const [sueldoCents, setSueldoCents] = useState(0)
  const [anchorDate, setAnchorDate] = useState('')

  // Fondo común
  const [aportacionCents, setAportacionCents] = useState(0)

  // Cuenta principal
  const [cuentaNombre, setCuentaNombre] = useState('Cuenta de nómina')
  const [cuentaSaldo, setCuentaSaldo] = useState(0)

  // Vales
  const [valesSaldo, setValesSaldo] = useState(0)

  // Tarjeta
  const [tarjetaNombre, setTarjetaNombre] = useState('')
  const [tarjetaLimite, setTarjetaLimite] = useState(0)
  const [tarjetaCorte, setTarjetaCorte] = useState('')
  const [tarjetaPlazo, setTarjetaPlazo] = useState('20')

  const pasos: Paso[] = esPersonal
    ? ['nomina', 'cuenta', 'vales', 'tarjeta', 'listo']
    : ['nomina', 'aportacion', 'cuenta', 'vales', 'tarjeta', 'listo']
  const indice = pasos.indexOf(paso)

  const avanzar = (): void => {
    haptic('light')
    setError(null)
    const siguiente = pasos[indice + 1]
    if (siguiente) setPaso(siguiente)
  }

  const retroceder = (): void => {
    haptic('light')
    setError(null)
    const anterior = pasos[indice - 1]
    if (anterior) setPaso(anterior)
  }

  const ejecutar = async (accion: () => Promise<unknown>, siguiente = true): Promise<void> => {
    setGuardando(true)
    setError(null)
    try {
      await accion()
      haptic('success')
      if (siguiente) avanzar()
    } catch (cause) {
      haptic('error')
      setError(cause instanceof ApiError ? cause.message : 'Algo salió mal. Inténtalo de nuevo.')
    } finally {
      setGuardando(false)
    }
  }

  const guardarNomina = (): Promise<void> =>
    ejecutar(() =>
      // Se crea como fuente `FIXED` llamada «Nómina». Quien además factura o
      // cobra comisiones añade las suyas desde Ajustes: pedirlas todas aquí
      // alargaría el alta justo donde hay que ganarse a quien todavía no sabe
      // si la app le sirve.
      incomes.create(memberId, {
        name: 'Nómina',
        kind: 'FIXED',
        frequency: frecuencia,
        netAmountCents: sueldoCents,
        // Quincenal: el 15 y el último día, que es lo más común en México.
        // Mensual: el último día. Ambos ajustables después desde Ajustes.
        anchorDayA: frecuencia === 'SEMI_MONTHLY' ? 15 : frecuencia === 'MONTHLY' ? 0 : 1,
        anchorDayB: frecuencia === 'SEMI_MONTHLY' ? 0 : null,
        anchorDate:
          frecuencia === 'BIWEEKLY' || frecuencia === 'WEEKLY'
            ? (anchorDate as never)
            : null,
      }),
    )

  const guardarAportacion = (): Promise<void> =>
    ejecutar(() => household.updateMember(memberId, { fixedContributionCents: aportacionCents }))

  const guardarCuenta = (): Promise<void> =>
    ejecutar(() =>
      accounts.create({
        name: cuentaNombre.trim() || 'Cuenta de nómina',
        type: 'CHECKING',
        ownerMemberId: memberId,
        initialBalanceCents: cuentaSaldo,
      }),
    )

  const guardarVales = (): Promise<void> =>
    ejecutar(() =>
      accounts.create({
        name: 'Vales de despensa',
        type: 'MEAL_VOUCHER',
        ownerMemberId: memberId,
        initialBalanceCents: valesSaldo,
      }),
    )

  const guardarTarjeta = (): Promise<void> =>
    ejecutar(() =>
      cards.create({
        name: tarjetaNombre.trim(),
        creditLimitCents: tarjetaLimite,
        statementDay: Number(tarjetaCorte),
        paymentTermDays: Number(tarjetaPlazo) || 20,
        ownerMemberId: memberId,
        isShared: false,
      }),
    )

  return (
    <div className="flex min-h-dvh flex-col bg-bg">
      <header className="bg-navy">
        <div className="px-safe mx-auto max-w-lg pb-6 pt-safe">
          <div className="flex items-center gap-3 pt-2">
            {indice > 0 && paso !== 'listo' ? (
              <button
                type="button"
                onClick={retroceder}
                aria-label="Paso anterior"
                className="flex size-10 items-center justify-center rounded-full bg-white/10 text-white active:scale-95"
              >
                <ArrowLeftIcon size={18} />
              </button>
            ) : null}
            <p className="text-[13px] font-medium text-white/60">
              Paso {indice + 1} de {pasos.length}
            </p>
          </div>

          {/* Barra de avance del asistente: es progreso de proceso, no un dato
              financiero, así que aquí una barra sí es la forma correcta. */}
          <div className="mt-3 flex gap-1.5" role="progressbar" aria-valuenow={indice + 1} aria-valuemin={1} aria-valuemax={pasos.length}>
            {pasos.map((p, i) => (
              <span
                key={p}
                className={cn(
                  'h-1 flex-1 rounded-pill transition-colors duration-300',
                  i <= indice ? 'bg-white' : 'bg-white/20',
                )}
              />
            ))}
          </div>
        </div>
      </header>

      <div className="-mt-4 flex-1 rounded-t-sheet bg-bg pt-6">
        <div className="px-safe mx-auto max-w-lg pb-8">
          {paso === 'nomina' ? (
            <Bloque
              titulo="¿Cómo te pagan?"
              descripcion="Con esto calculamos tu periodo de gasto y te avisamos el día que cobras."
            >
              <div className="grid grid-cols-2 gap-2">
                {FRECUENCIAS.map((f) => (
                  <Opcion
                    key={f.value}
                    activo={frecuencia === f.value}
                    onClick={() => {
                      haptic('selection')
                      setFrecuencia(f.value)
                    }}
                    titulo={f.label}
                    detalle={f.detalle}
                  />
                ))}
              </div>

              {frecuencia === 'BIWEEKLY' || frecuencia === 'WEEKLY' ? (
                <Campo
                  label="Fecha de tu último pago"
                  ayuda="La necesitamos para calcular los siguientes: catorcenal y semanal no caen en un día fijo del mes."
                >
                  <input
                    type="date"
                    value={anchorDate}
                    onChange={(e) => setAnchorDate(e.target.value)}
                    className="min-h-[48px] w-full rounded-card border border-line bg-surface px-3.5 text-[16px] text-ink"
                  />
                </Campo>
              ) : (
                <p className="rounded-card bg-surface-sunken px-3 py-2.5 text-[12px] leading-snug text-ink-soft">
                  {frecuencia === 'SEMI_MONTHLY'
                    ? 'Tomamos el 15 y el último día del mes. Si cae en fin de semana o festivo, adelantamos al día hábil anterior.'
                    : 'Tomamos el último día del mes. Si cae en inhábil, adelantamos al día hábil anterior.'}
                </p>
              )}

              <MontoConTeclado
                label="Cuánto recibes cada pago (neto)"
                cents={sueldoCents}
                onChange={setSueldoCents}
              />

              <Avanzar
                onClick={guardarNomina}
                disabled={
                  sueldoCents <= 0 ||
                  ((frecuencia === 'BIWEEKLY' || frecuencia === 'WEEKLY') && !anchorDate)
                }
                guardando={guardando}
              >
                Continuar
              </Avanzar>
            </Bloque>
          ) : null}

          {paso === 'aportacion' ? (
            <Bloque
              titulo="Tu aportación al fondo común"
              descripcion="Cuánto pones cada mes para los gastos compartidos. Se resta de tu dinero libre y se suma al del hogar."
              Icon={UsersThreeIcon}
            >
              <MontoConTeclado
                label="Monto fijo mensual"
                cents={aportacionCents}
                onChange={setAportacionCents}
              />
              <Avanzar onClick={guardarAportacion} guardando={guardando}>
                Continuar
              </Avanzar>
              <Saltar onClick={avanzar}>Lo defino después</Saltar>
            </Bloque>
          ) : null}

          {paso === 'cuenta' ? (
            <Bloque
              titulo="Tu cuenta principal"
              descripcion="Donde cae tu nómina. De aquí sale el dinero disponible."
              Icon={WalletIcon}
            >
              <Campo label="Nombre">
                <input
                  value={cuentaNombre}
                  onChange={(e) => setCuentaNombre(e.target.value)}
                  placeholder="Cuenta de nómina"
                  className="min-h-[48px] w-full rounded-card border border-line bg-surface px-3.5 text-[16px] text-ink placeholder:text-ink-faint"
                />
              </Campo>
              <MontoConTeclado
                label="Saldo actual"
                cents={cuentaSaldo}
                onChange={setCuentaSaldo}
              />
              <Avanzar onClick={guardarCuenta} guardando={guardando}>
                Continuar
              </Avanzar>
            </Bloque>
          ) : null}

          {paso === 'vales' ? (
            <Bloque
              titulo="¿Recibes vales de despensa?"
              descripcion="Los guardamos en una bolsa aparte: son dinero real, pero solo compran despensa, y sumarlos al efectivo mentiría sobre lo que puedes gastar."
              Icon={BasketIcon}
            >
              <MontoConTeclado
                label="Saldo de tus vales"
                cents={valesSaldo}
                onChange={setValesSaldo}
              />
              <Avanzar onClick={guardarVales} disabled={valesSaldo <= 0} guardando={guardando}>
                Continuar
              </Avanzar>
              <Saltar onClick={avanzar}>No recibo vales</Saltar>
            </Bloque>
          ) : null}

          {paso === 'tarjeta' ? (
            <Bloque
              titulo="Tu tarjeta de crédito"
              descripcion="Con la fecha de corte calculamos cuándo vence tu pago, en días hábiles y saltando festivos bancarios."
              Icon={CreditCardIcon}
            >
              <Campo label="Nombre de la tarjeta">
                <input
                  value={tarjetaNombre}
                  onChange={(e) => setTarjetaNombre(e.target.value)}
                  placeholder="BBVA Azul"
                  className="min-h-[48px] w-full rounded-card border border-line bg-surface px-3.5 text-[16px] text-ink placeholder:text-ink-faint"
                />
              </Campo>

              <div className="grid grid-cols-2 gap-3">
                <Campo label="Día de corte" ayuda="Del 1 al 31">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={31}
                    value={tarjetaCorte}
                    onChange={(e) => setTarjetaCorte(e.target.value)}
                    placeholder="5"
                    className="min-h-[48px] w-full rounded-card border border-line bg-surface px-3.5 text-[16px] text-ink placeholder:text-ink-faint"
                  />
                </Campo>
                <Campo label="Días para pagar" ayuda="Hábiles">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={tarjetaPlazo}
                    onChange={(e) => setTarjetaPlazo(e.target.value)}
                    className="min-h-[48px] w-full rounded-card border border-line bg-surface px-3.5 text-[16px] text-ink"
                  />
                </Campo>
              </div>

              <MontoConTeclado
                label="Límite de crédito"
                cents={tarjetaLimite}
                onChange={setTarjetaLimite}
              />

              <Avanzar
                onClick={guardarTarjeta}
                disabled={
                  !tarjetaNombre.trim() ||
                  tarjetaLimite <= 0 ||
                  !tarjetaCorte ||
                  Number(tarjetaCorte) < 1 ||
                  Number(tarjetaCorte) > 31
                }
                guardando={guardando}
              >
                Continuar
              </Avanzar>
              <Saltar onClick={avanzar}>No tengo tarjetas</Saltar>
            </Bloque>
          ) : null}

          {paso === 'listo' ? (
            <div className="flex flex-col items-center gap-5 pt-6 text-center">
              <span className="flex size-16 items-center justify-center rounded-full bg-income-soft text-income-text">
                <CheckIcon size={32} weight="bold" />
              </span>
              <div>
                <h2 className="font-display text-2xl font-extrabold tracking-[-0.03em] text-ink">
                  Todo listo
                </h2>
                <p className="mt-2 text-[15px] leading-snug text-ink-soft">
                  Ya podemos calcular cuánto tienes disponible. Lo que falte lo
                  completas cuando quieras desde Ajustes.
                </p>
              </div>
              {/* Sin flecha: el chevrón de la pastilla ya dice «adelante». */}
              <Pastilla
                onClick={() => {
                  haptic('success')
                  onDone()
                }}
              >
                Ver mi tablero
              </Pastilla>
            </div>
          ) : null}

          {error ? (
            <p role="alert" className="mt-4 rounded-card bg-expense-soft px-3 py-2.5 text-[13px] text-expense-text">
              {error}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function Bloque({
  titulo,
  descripcion,
  Icon,
  children,
}: {
  titulo: string
  descripcion: string
  Icon?: typeof WalletIcon
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-2">
        {Icon ? (
          <span className="flex size-11 items-center justify-center rounded-full bg-primary-soft text-primary-text">
            <Icon size={22} weight="fill" />
          </span>
        ) : null}
        <h2 className="font-display text-[24px] font-extrabold leading-tight tracking-[-0.03em] text-ink">
          {titulo}
        </h2>
        <p className="text-[14px] leading-snug text-ink-soft">{descripcion}</p>
      </header>
      {children}
    </div>
  )
}

function Opcion({
  activo,
  onClick,
  titulo,
  detalle,
}: {
  activo: boolean
  onClick: () => void
  titulo: string
  detalle: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activo}
      className={cn(
        'flex min-h-[68px] flex-col items-start justify-center gap-0.5 rounded-card border px-3.5 py-2.5 text-left',
        'transition-colors duration-150 active:scale-[0.98]',
        activo ? 'border-primary bg-primary-soft' : 'border-line bg-surface',
      )}
    >
      <span className={cn('text-[15px] font-semibold', activo ? 'text-ink' : 'text-ink-soft')}>
        {titulo}
      </span>
      <span className="text-[12px] text-ink-faint">{detalle}</span>
    </button>
  )
}

function Campo({
  label,
  ayuda,
  children,
}: {
  label: string
  ayuda?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[13px] font-medium text-ink">{label}</label>
      {children}
      {ayuda ? <p className="text-[12px] leading-snug text-ink-faint">{ayuda}</p> : null}
    </div>
  )
}

/**
 * Monto con teclado propio.
 *
 * Se reutiliza el mismo teclado de la captura de gastos: aprender un solo gesto
 * para escribir dinero en toda la app vale más que ahorrar unos píxeles con el
 * teclado del sistema, que además tapa media pantalla en iOS.
 */
function MontoConTeclado({
  label,
  cents,
  onChange,
}: {
  label: string
  cents: number
  onChange: React.Dispatch<React.SetStateAction<number>>
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-[13px] font-medium text-ink">{label}</span>
      <AmountDisplay cents={cents} />
      <NumPad valueCents={cents} onChange={onChange} />
    </div>
  )
}

function Avanzar({
  onClick,
  disabled = false,
  guardando,
  children,
}: {
  onClick: () => void
  disabled?: boolean
  guardando: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || guardando}
      className={cn(
        // La misma forma que `Guardar` y `Pastilla`: el asistente es lo primero
        // que se ve de la app, y si su botón no se parece al del resto, el
        // sistema se aprende dos veces.
        'mt-1 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-pill text-base font-bold',
        'transition-transform duration-150 active:scale-[0.97]',
        disabled || guardando
          ? 'cursor-not-allowed bg-surface-sunken text-ink-faint'
          : 'bg-accion text-on-accion',
      )}
    >
      {guardando ? (
        <SpinnerGapIcon size={20} className="animate-spin" />
      ) : (
        <>
          {children}
          {!disabled ? (
            <span aria-hidden="true" className="text-[17px] font-black leading-none text-accion-acento">
              »
            </span>
          ) : null}
        </>
      )}
    </button>
  )
}

function Saltar({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="min-h-[44px] text-center text-[14px] font-medium text-ink-soft underline underline-offset-4"
    >
      {children}
    </button>
  )
}
